import React, { useState, useEffect } from 'react';
import { Tab, Tabs, Form, Button, Container, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import ErrorModal from '../Modals/ErrorModal';
import SuccessModal from '../Modals/SuccessModal';
import { useUserPreferences } from '../../context/UserPreferencesContext';
import { usePinVerification } from '../../context/PinVerificationContext';
import useProfileForm from '../../hooks/Users/useProfileForm';
import { clearAllAuthData, usernameStorage } from '../../utils/encryptedCookie';

const Profile = () => {
  const {
    bgColor,
    textColor,
    setBgColor,
    setTextColor,
    savePreferences,
  } = useUserPreferences();

  const {
    register,
    handleSubmit,
    errors,
    handlePasswordUpdate,
    handlePinUpdate,
    fetchUserData,
  } = useProfileForm();

  const {
    register: registerPin,
    handleSubmit: handleSubmitPin,
    formState: { errors: pinErrors },
    reset: resetPinForm,
  } = useForm();

  const { requirePinForUpdate } = usePinVerification();

  const [activeTab, setActiveTab] = useState('general');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [onAfterClose, setOnAfterClose] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isPinActive, setIsPinActive] = useState(false);
  const [isLoadingPin, setIsLoadingPin] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.documentElement.style.setProperty('--bg-color', bgColor);
    document.documentElement.style.setProperty('--text-color', textColor);
  }, [bgColor, textColor]);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const data = await fetchUserData();
        if (data) {
          setIsPinActive(data.isPin || false);
        }
      } catch (error) {
        console.error('Erro ao carregar dados do usuário:', error);
      }
    };
    loadUserData();
  }, [fetchUserData]);

  const handleSavePreferences = async () => {
    try {
      await savePreferences(bgColor, textColor);
      setSuccessMessage('Preferências salvas com sucesso!');
      setOnAfterClose(false);
      setShowSuccess(true);
    } catch (error) {
      setErrorMessage('Erro ao salvar preferências: ' + error.message);
      setShowError(true);
    }
  };

  const handlePasswordSubmit = async (data) => {
    try {
      const success = await handlePasswordUpdate(data);
      if (success) {
        setSuccessMessage('Senha alterada com sucesso!');
        setOnAfterClose(true);
        setShowSuccess(true);
      }
    } catch (error) {
      setErrorMessage(error.message);
      setShowError(true);
    }
  };

  const handlePinSubmit = async (data) => {
    setIsLoadingPin(true);
    try {
      if (!data.pin || data.pin.length !== 4) {
        setErrorMessage('O PIN deve conter exatamente 4 dígitos.');
        setShowError(true);
        setIsLoadingPin(false);
        return;
      }

      if (data.pin !== data.confirmPin) {
        setErrorMessage('Os PINs não coincidem.');
        setShowError(true);
        setIsLoadingPin(false);
        return;
      }

      let currentPin = null;
      
      if (isPinActive) {
        try {
          currentPin = await requirePinForUpdate();
        } catch (error) {
          setErrorMessage('Erro ao verificar PIN atual: ' + error.message);
          setShowError(true);
          setIsLoadingPin(false);
          return;
        }
      }

      const updatedData = await handlePinUpdate(data.pin, currentPin);
      if (updatedData) {
        setIsPinActive(updatedData.isPin || false);
        setSuccessMessage(isPinActive ? 'PIN alterado com sucesso!' : 'PIN configurado com sucesso!');
        setOnAfterClose(false);
        setShowSuccess(true);
        resetPinForm();
      }
    } catch (error) {
      setErrorMessage(error.message);
      setShowError(true);
    } finally {
      setIsLoadingPin(false);
    }
  };

  const handleCloseError = () => setShowError(false);
  const handleCloseSuccess = () => {
    setShowSuccess(false);
  };

  return (
    <Container>
      <h3 className="mb-4 mt-2 text-center text-md-start">Perfil</h3>
      <Tabs
        id="profile-tabs"
        activeKey={activeTab}
        onSelect={(tab) => setActiveTab(tab)}
        className="mb-3 custom-tabs"
      >
        <Tab eventKey="general" title="Preferências">
          <Form>
            <Form.Group controlId="bgColor" className="mb-3">
              <Form.Label className="mb-1">Cor de Fundo</Form.Label>
              <Form.Control
                type="color"
                value={bgColor}
                style={{ padding: '0' }}
                onChange={(e) => setBgColor(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="textColor" className="mb-3">
              <Form.Label className="mb-1">Cor do Texto</Form.Label>
              <Form.Control
                type="color"
                value={textColor}
                style={{ padding: '0' }}
                onChange={(e) => setTextColor(e.target.value)}
              />
            </Form.Group>
            <Button variant="primary" className="button-custom" onClick={handleSavePreferences}>
              Salvar Preferências
            </Button>
          </Form>
        </Tab>
        <Tab eventKey="password" title="Alterar Senha">
          <Form onSubmit={handleSubmit(handlePasswordSubmit)}>
            <Form.Group controlId="formCurrentPassword" className="mb-3">
              <Form.Label className="mb-1">Senha Atual</Form.Label>
              <Form.Control
                type="password"
                {...register('currentPassword', { required: 'A senha atual é obrigatória' })}
                isInvalid={!!errors.currentPassword}
              />
              <Form.Control.Feedback type="invalid">
                {errors.currentPassword && errors.currentPassword.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="formNewPassword" className="mb-3">
              <Form.Label className="mb-1">Nova Senha</Form.Label>
              <Form.Control
                type="password"
                {...register('newPassword', { required: 'A nova senha é obrigatória' })}
                isInvalid={!!errors.newPassword}
              />
              <Form.Control.Feedback type="invalid">
                {errors.newPassword && errors.newPassword.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="formConfirmPassword" className="mb-3">
              <Form.Label className="mb-1">Confirmar Nova Senha</Form.Label>
              <Form.Control
                type="password"
                {...register('confirmPassword', { required: 'Confirme a nova senha' })}
                isInvalid={!!errors.confirmPassword}
              />
              <Form.Control.Feedback type="invalid">
                {errors.confirmPassword && errors.confirmPassword.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Button variant="primary" className="button-custom" type="submit">
              Alterar Senha
            </Button>
          </Form>
        </Tab>
        <Tab eventKey="pin" title="PIN">
          <div className="mb-3">
            <Alert variant={isPinActive ? 'success' : 'warning'}>
              <strong>Status do PIN:</strong> {isPinActive ? 'Ativo' : 'Inativo'}
            </Alert>
          </div>
          <Form onSubmit={handleSubmitPin(handlePinSubmit)}>
            <Form.Group controlId="formPin" className="mb-3">
              <Form.Label className="mb-1">
                {isPinActive ? 'Alterar PIN' : 'Configurar PIN'}
              </Form.Label>
              <Form.Control
                type="password"
                maxLength={4}
                placeholder="Digite 4 dígitos"
                {...registerPin('pin', {
                  required: 'O PIN é obrigatório',
                  minLength: {
                    value: 4,
                    message: 'O PIN deve conter exatamente 4 dígitos',
                  },
                  maxLength: {
                    value: 4,
                    message: 'O PIN deve conter exatamente 4 dígitos',
                  },
                  pattern: {
                    value: /^\d+$/,
                    message: 'O PIN deve conter apenas números',
                  },
                })}
                isInvalid={!!pinErrors.pin}
              />
              <Form.Control.Feedback type="invalid">
                {pinErrors.pin && pinErrors.pin.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="formConfirmPin" className="mb-3">
              <Form.Label className="mb-1">Confirmar PIN</Form.Label>
              <Form.Control
                type="password"
                maxLength={4}
                placeholder="Confirme os 4 dígitos"
                {...registerPin('confirmPin', {
                  required: 'Confirme o PIN',
                  minLength: {
                    value: 4,
                    message: 'O PIN deve conter exatamente 4 dígitos',
                  },
                  maxLength: {
                    value: 4,
                    message: 'O PIN deve conter exatamente 4 dígitos',
                  },
                  pattern: {
                    value: /^\d+$/,
                    message: 'O PIN deve conter apenas números',
                  },
                })}
                isInvalid={!!pinErrors.confirmPin}
              />
              <Form.Control.Feedback type="invalid">
                {pinErrors.confirmPin && pinErrors.confirmPin.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Button
              variant="primary"
              className="button-custom"
              type="submit"
              disabled={isLoadingPin}
            >
              {isLoadingPin ? 'Salvando...' : isPinActive ? 'Alterar PIN' : 'Configurar PIN'}
            </Button>
          </Form>
        </Tab>
      </Tabs>

      <ErrorModal
        show={showError}
        handleClose={handleCloseError}
        errorMessage={errorMessage}
      />

      <SuccessModal
        show={showSuccess}
        handleClose={handleCloseSuccess}
        successMessage={successMessage}
        onAfterClose={() => {
          if (onAfterClose) {
            const username = usernameStorage.get() || '';
            clearAllAuthData();
            if (username) {
              sessionStorage.removeItem(`pinVerified_${username}`);
            }
            sessionStorage.clear();
            navigate('/login');
          }
        }}
      />
    </Container>
  );
};

export default Profile;